import { Category } from '@api/models/category';
import { SalesArticleSummary } from '@api/models/sales-article-summary';
import { CategoryFlat } from '@api/models/category-flat';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { map, tap } from 'rxjs/operators';
import {
  ClearSalesArticlePagePosition,
  LoadCategories,
  LoadHighlightedCategories,
  LoadHighlightedSalesArticles,
  LoadLatest,
  LoadPopular,
  LoadPopularByCategory,
  LoadPopularForMainCategory,
  LoadSalesArticlesByBrand,
  LoadSalesArticlesByCategoryStarted,
  LoadTemporaryDiscounts
} from './box-deals.actions';
import { SalesArticlesService } from '@api/services/sales-articles.service';
import { CategoriesService } from '@api/services/categories.service';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { RecommendationsService } from '@api/services';

export class BoxDealsStateModel {
  highlightedSalesArticles: SalesArticleSummary[];
  highlightedCategories: CategoryFlat[];
  salesArticles: SalesArticleSummary[];
  popular: SalesArticleSummary[];
  latest: SalesArticleSummary[];
  temporaryDiscounts: SalesArticleSummary[];
  categories: Category[];
  salesArticlePagePosition: number;
  salesArticlePageEnd: boolean;
  salesArticleLoading: boolean;
  popularByCategory: Map<string, SalesArticleSummary[]>;
}

@State<BoxDealsStateModel>({
  name: 'box_deals',
  defaults: {
    highlightedSalesArticles: [],
    highlightedCategories: [],
    salesArticles: [],
    popular: [],
    latest: [],
    temporaryDiscounts: [],
    categories: [],
    salesArticlePagePosition: 0,
    salesArticlePageEnd: false,
    salesArticleLoading: false,
    popularByCategory: new Map<string, SalesArticleSummary[]>()
  }
})
@Injectable()
export class BoxDealsState {
  private takeSalesArticles = 20;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private salesArticlesService: SalesArticlesService,
    private categoriesService: CategoriesService,
    private recommendationService: RecommendationsService
  ) {
    if (isPlatformServer(this.platformId)) {
      this.takeSalesArticles = 400;
    }
  }

  @Selector()
  static selectHighLightedBoxdeals(state: BoxDealsStateModel): SalesArticleSummary[] {
    return state.highlightedSalesArticles;
  }

  @Selector()
  static selectHighLightedCategories(state: BoxDealsStateModel): CategoryFlat[] {
    return state.highlightedCategories;
  }

  @Selector()
  static selectSalesArticles(state: BoxDealsStateModel): SalesArticleSummary[] {
    return state.salesArticles;
  }

  @Selector()
  static selectCategories(state: BoxDealsStateModel): Category[] {
    return state.categories;
  }

  @Selector()
  static selectSalesArticlePageEnd(state: BoxDealsStateModel): boolean {
    return state.salesArticlePageEnd;
  }

  @Selector()
  static selectSalesArticlePagePosition(state: BoxDealsStateModel): number {
    return state.salesArticlePagePosition;
  }

  @Selector()
  static selectSalesArticleLoading(state: BoxDealsStateModel): boolean {
    return state.salesArticleLoading;
  }

  @Selector()
  static selectPopular(state: BoxDealsStateModel): SalesArticleSummary[] {
    return state.popular;
  }

  @Selector()
  static selectLatest(state: BoxDealsStateModel): SalesArticleSummary[] {
    return state.latest;
  }

  @Selector()
  static selectPopularByCategory(state: BoxDealsStateModel): Map<string, SalesArticleSummary[]> {
    return state.popularByCategory;
  }

  @Selector()
  static selectTemporaryDiscounts(state: BoxDealsStateModel): SalesArticleSummary[] {
    return state.temporaryDiscounts;
  }

  @Action(LoadHighlightedSalesArticles, { cancelUncompleted: true })
  private loadHighlightedBoxDeals(ctx: StateContext<BoxDealsStateModel>) {
    return this.salesArticlesService.apiProductsquerySalesarticlesHighlightedGet().pipe(
      map((response: SalesArticleSummary[]) => {
        ctx.patchState({ highlightedSalesArticles: response });
      })
    );
  }

  @Action(LoadHighlightedCategories, { cancelUncompleted: true })
  private loadHighlightedCategories(ctx: StateContext<BoxDealsStateModel>) {
    return this.categoriesService
      .apiProductsqueryCategoriesHighlightedGet()
      .subscribe((response) => ctx.patchState({ highlightedCategories: response }));
  }

  @Action(LoadCategories, { cancelUncompleted: true })
  private loadCategories({ patchState }: StateContext<BoxDealsStateModel>) {
    return this.categoriesService
      .apiProductsqueryCategoriesGet()
      .pipe(tap((response) => patchState({ categories: response })));
  }

  @Action(ClearSalesArticlePagePosition)
  private clearSalesArticlePagePosition({ patchState }: StateContext<BoxDealsStateModel>) {
    return patchState({ salesArticlePagePosition: 0, salesArticlePageEnd: false, salesArticles: [] });
  }

  @Action(LoadSalesArticlesByCategoryStarted, { cancelUncompleted: true })
  private loadSalesArticleByCategoryStart(
    ctx: StateContext<BoxDealsStateModel>,
    action: LoadSalesArticlesByCategoryStarted
  ) {
    ctx.patchState({ salesArticleLoading: true });
    action.payload.take = 400;
    action.payload.skip = 0;
    return this.salesArticlesService.apiProductsquerySalesarticlesCategoryCategoryIdGet(action.payload).pipe(
      map((response: SalesArticleSummary[]) => {
        if (response.length === 0) {
          return ctx.patchState({ salesArticlePageEnd: true, salesArticleLoading: false });
        } else {
          return ctx.patchState({
            salesArticles: response,
            salesArticleLoading: false
          });
        }
      })
    );
  }

  @Action(LoadSalesArticlesByBrand, { cancelUncompleted: true })
  private loadSalesArticlesByBrand(ctx: StateContext<BoxDealsStateModel>, action: LoadSalesArticlesByBrand) {
    ctx.patchState({ salesArticles: [], salesArticleLoading: true });
    return this.salesArticlesService.apiProductsquerySalesarticlesBrandBrandGet({ brand: action.payload }).pipe(
      map((val) => {
        ctx.patchState({ salesArticles: val, salesArticleLoading: false });
      })
    );
  }

  @Action(LoadPopular, { cancelUncompleted: true })
  private loadPopular(ctx: StateContext<BoxDealsStateModel>) {
    return this.recommendationService.apiProductsqueryRecommendationsPopularGet().pipe(
      map((val) => {
        ctx.patchState({ popular: val, salesArticleLoading: false });
      })
    );
  }

  @Action(LoadLatest, { cancelUncompleted: true })
  private loadLatest(ctx: StateContext<BoxDealsStateModel>) {
    return this.recommendationService.apiProductsqueryRecommendationsLatestGet().pipe(
      map((val) => {
        ctx.patchState({ latest: val, salesArticleLoading: false });
      })
    );
  }

  @Action(LoadPopularForMainCategory, { cancelUncompleted: true })
  private loadPopularForMainCategory(ctx: StateContext<BoxDealsStateModel>, action: LoadPopularForMainCategory) {
    return this.recommendationService
      .apiProductsqueryRecommendationsPopularLabelsGet({ labels: [action.categoryId] })
      .pipe(
        map((val) => {
          ctx.patchState({ salesArticles: val[action.categoryId] });
        })
      );
  }

  @Action(LoadPopularByCategory, { cancelUncompleted: true })
  private loadPopularByCategory(ctx: StateContext<BoxDealsStateModel>, action: LoadPopularByCategory) {
    return this.recommendationService.apiProductsqueryRecommendationsPopularCategoriesIdGet({ id: action.id }).pipe(
      map((val) => {
        ctx.patchState({ salesArticles: val });
      })
    );
  }

  @Action(LoadTemporaryDiscounts, { cancelUncompleted: true })
  private loadTemporaryDiscounts(ctx: StateContext<BoxDealsStateModel>) {
    return this.salesArticlesService.apiProductsquerySalesarticlesTemporarydiscountsGet().pipe(
      map((val) => {
        ctx.patchState({ temporaryDiscounts: val });
      })
    );
  }
}
